import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { db } from '../db';
import { contentHash } from '../db/contentHash';

// Reason: default adapter works for the `meta` table where PK is `key`;
// entity-specific callers must supply toRecord to set their own PK fields
// and any mirrored index columns (e.g. is_read as 0/1 for articles).
const defaultToRecord = (payload, meta) => ({ ...meta, payload });

/**
 * Local-first read hook.
 *
 * @param {object} opts
 * @param {string} opts.table   - Dexie table name.
 * @param {string|number} opts.key - Primary-key value.
 * @param {number} opts.ttl    - Freshness window in ms.
 * @param {Function} opts.fetch - async ({etag}) => {status, etag, payload}. 304 means no change.
 * @param {Function} [opts.toRecord] - (payload, meta) => row. Callers MUST memoize with useCallback.
 * @returns {{ data, loading, error, refresh }}
 */
export const useLocalFirstQuery = ({ table, key, ttl, fetch, toRecord = defaultToRecord }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Reason: generation counter replaces a single inFlightRef boolean so that a
  // slow fetch for an old key cannot overwrite state or block a new key's fetch.
  const generationRef = useRef(0);

  const runFetch = useCallback(async (cached, gen) => {
    try {
      const resp = await fetch({ etag: cached?.etag ?? null });
      if (gen !== generationRef.current) return;
      const now = Date.now();
      if (resp.status === 304) {
        // Reason: only update fetched_at — row exists by construction (we have a cached row).
        await db.table(table).update(key, { fetched_at: now });
        return;
      }
      if (resp.status !== 200) {
        throw new Error(`Unexpected response status ${resp.status}`);
      }
      const newHash = await contentHash(resp.payload);
      if (gen !== generationRef.current) return;
      if (cached && cached.content_hash === newHash) {
        // Reason: same content, refresh etag + fetched_at without touching payload or state.
        await db.table(table).update(key, { fetched_at: now, etag: resp.etag ?? cached.etag ?? null });
        return;
      }
      const row = toRecord(resp.payload, {
        key,
        etag: resp.etag ?? null,
        fetched_at: now,
        content_hash: newHash,
        previous: cached,
      });
      await db.table(table).put(row);
      setData(resp.payload);
      setError(null);
    } catch (err) {
      if (gen !== generationRef.current) return;
      if (!cached) {
        // No fallback — surface the error.
        setError(err);
        setData(null);
      }
      // else: silent — stale cache stays on screen.
    } finally {
      if (gen === generationRef.current) {
        setLoading(false);
      }
    }
  }, [table, key, fetch, toRecord]);

  useEffect(() => {
    let cancelled = false;
    const gen = ++generationRef.current;
    (async () => {
      try {
        const cached = await db.table(table).get(key);
        if (cancelled) return;
        if (cached) {
          setData(cached.payload);
          setLoading(false);
          const isFresh = (Date.now() - cached.fetched_at) < ttl;
          if (!isFresh) runFetch(cached, gen);
        } else {
          setLoading(true);
          await runFetch(null, gen);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err);
          setData(null);
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [table, key, ttl, runFetch]);

  const refresh = useCallback(async () => {
    const gen = ++generationRef.current;
    const cached = await db.table(table).get(key);
    await runFetch(cached ?? null, gen);
  }, [table, key, runFetch]);

  return useMemo(() => ({ data, loading, error, refresh }), [data, loading, error, refresh]);
};

export default useLocalFirstQuery;
